/**
 * SupplierScorecardBoard
 * ----------------------
 * Ranks the bench by behaviour, not by invoice size: who answers an RFQ, how
 * fast, who wins, and who actually ships on the date they promised. Every
 * figure is derived from records the system already keeps — RFQ tokens, quote
 * submissions, award records, supplier orders and the payables POs.
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  Loader2, Download, Trophy, Clock, Truck, Percent, AlertTriangle, X,
  ArrowUpDown, Mail, Phone, Sparkles,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { API } from "@/App";

const WINDOWS = [
  { days: 90, label: "90 days" },
  { days: 180, label: "6 months" },
  { days: 365, label: "12 months" },
  { days: 1095, label: "3 years" },
];

const GRADE_CLS = {
  A: "bg-emerald-500 text-white",
  B: "bg-lime-500 text-white",
  C: "bg-amber-500 text-white",
  D: "bg-orange-500 text-white",
  F: "bg-red-500 text-white",
};

const money = (v) => `$${(Number(v) || 0).toLocaleString(undefined, { maximumFractionDigits: 0 })}`;
const pct = (v) => (v === null || v === undefined ? "—" : `${v}%`);
const num = (v, suffix = "") => (v === null || v === undefined ? "—" : `${v}${suffix}`);
const when = (iso) => (iso ? new Date(iso).toLocaleDateString() : "—");

const COLUMNS = [
  { key: "rank", label: "#", align: "left" },
  { key: "supplier_name", label: "Supplier", align: "left" },
  { key: "score", label: "Score", align: "left" },
  { key: "rfqs_invited", label: "RFQs", align: "right" },
  { key: "quotes_returned", label: "Quoted", align: "right" },
  { key: "quote_rate_pct", label: "Quote rate", align: "right" },
  { key: "median_response_hours", label: "Responds in", align: "right" },
  { key: "awards_won", label: "Won", align: "right" },
  { key: "win_rate_pct", label: "Win rate", align: "right" },
  { key: "on_time_pct", label: "On time", align: "right" },
  { key: "avg_days_to_ship", label: "Days to ship", align: "right" },
  { key: "price_index", label: "Price vs peers", align: "right" },
  { key: "last_quote_at", label: "Last quote", align: "right" },
];

export default function SupplierScorecardBoard({ token }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [days, setDays] = useState(365);
  const [minRfqs, setMinRfqs] = useState(3);
  const [sort, setSort] = useState({ key: "score", dir: "desc" });
  const [openId, setOpenId] = useState(null);

  const headers = useMemo(() => ({ Authorization: `Bearer ${token}` }), [token]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/admin/supplier-scorecard`, {
        params: { days, min_rfqs: minRfqs },
        headers: { Authorization: `Bearer ${token}` },
      });
      setData(r.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not build the scorecard");
    } finally { setLoading(false); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [days, minRfqs, token]);

  useEffect(() => { load(); }, [load]);

  const rows = data?.suppliers || [];
  const graded = rows.filter((r) => r.score !== null);
  const pending = rows.filter((r) => r.score === null);
  const totals = data?.totals || {};

  const sorted = useMemo(() => {
    const dir = sort.dir === "asc" ? 1 : -1;
    return [...graded].sort((a, b) => {
      const av = a[sort.key], bv = b[sort.key];
      if (av === bv) return 0;
      if (av === null || av === undefined) return 1;
      if (bv === null || bv === undefined) return -1;
      if (typeof av === "string") return av.localeCompare(bv) * dir;
      return (av - bv) * dir;
    });
  }, [graded, sort]);

  const flip = (key) =>
    setSort((s) => ({ key, dir: s.key === key && s.dir === "desc" ? "asc" : "desc" }));

  const exportCsv = async () => {
    try {
      const r = await axios.get(`${API}/admin/supplier-scorecard/export`, {
        params: { days, min_rfqs: minRfqs }, headers, responseType: "blob",
      });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `supplier-scorecard-${days}d.csv`;
      a.click();
      URL.revokeObjectURL(url);
    } catch { toast.error("Could not build the export"); }
  };

  return (
    <div className="space-y-5" data-testid="supplier-scorecard-board">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h2 className="text-xl font-black text-slate-900 inline-flex items-center gap-2">
            <Trophy className="w-5 h-5 text-amber-500" /> Supplier Scorecard
          </h2>
          <p className="text-sm text-slate-500 mt-0.5">
            Who answers, who wins, and who ships when they said they would.
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <div className="inline-flex rounded-lg border border-slate-200 overflow-hidden">
            {WINDOWS.map((w) => (
              <button key={w.days} onClick={() => setDays(w.days)}
                className={`px-3 py-1.5 text-xs font-bold transition-colors ${
                  days === w.days ? "bg-slate-900 text-white" : "bg-white text-slate-600 hover:bg-slate-50"}`}
                data-testid={`scorecard-window-${w.days}`}>
                {w.label}
              </button>
            ))}
          </div>
          <label className="text-xs text-slate-600 inline-flex items-center gap-1.5">
            Grade after
            <select value={minRfqs} onChange={(e) => setMinRfqs(Number(e.target.value))}
              className="h-8 text-xs border border-slate-300 rounded-md px-1"
              data-testid="scorecard-min-rfqs">
              {[0, 1, 3, 5, 10].map((n) => <option key={n} value={n}>{n} RFQs</option>)}
            </select>
          </label>
          <Button size="sm" variant="outline" onClick={exportCsv} data-testid="scorecard-export">
            <Download className="w-3.5 h-3.5 mr-1" /> CSV
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-5 gap-3">
        <Stat icon={Percent} tone="text-blue-700 bg-blue-50" label="Bench quote rate"
          value={pct(totals.overall_quote_rate_pct)} testId="scorecard-quote-rate"
          sub={`${totals.quotes_returned || 0} of ${totals.rfqs_invited || 0} RFQs answered`} />
        <Stat icon={Trophy} tone="text-amber-700 bg-amber-50" label="Average score"
          value={totals.avg_score === null || totals.avg_score === undefined ? "—" : totals.avg_score}
          sub={`${totals.graded || 0} suppliers graded`} />
        <Stat icon={Sparkles} tone="text-emerald-700 bg-emerald-50" label="Awards in window"
          value={totals.awards_won || 0} sub="orders actually placed" />
        <Stat icon={Clock} tone="text-slate-700 bg-slate-100" label="Awaiting history"
          value={totals.needs_history || 0} sub={`fewer than ${minRfqs} RFQs`} />
        <Stat icon={AlertTriangle} tone="text-red-700 bg-red-50" label="POs past need-by"
          value={totals.open_overdue_pos || 0} sub="still open and late" />
      </div>

      {loading ? (
        <p className="text-slate-500 inline-flex items-center gap-2 py-10">
          <Loader2 className="w-4 h-4 animate-spin" /> Crunching RFQs, quotes, awards and ship dates…
        </p>
      ) : (
        <>
          <div className="border border-slate-200 rounded-lg overflow-x-auto bg-white">
            <table className="w-full min-w-[1080px] text-sm">
              <thead className="bg-slate-50">
                <tr>
                  {COLUMNS.map((c) => (
                    <th key={c.key}
                      onClick={() => flip(c.key)}
                      className={`px-3 py-2 text-[10px] font-black uppercase tracking-wider text-slate-500
                        cursor-pointer select-none hover:text-slate-900 whitespace-nowrap text-${c.align}`}
                      data-testid={`scorecard-sort-${c.key}`}>
                      <span className="inline-flex items-center gap-1">
                        {c.label}
                        {sort.key === c.key && <ArrowUpDown className="w-3 h-3" />}
                      </span>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {sorted.length === 0 && (
                  <tr><td colSpan={COLUMNS.length} className="px-3 py-8 text-center text-slate-500">
                    No supplier has enough history in this window yet. Widen the window or lower the RFQ threshold.
                  </td></tr>
                )}
                {sorted.map((r) => (
                  <tr key={r.supplier_id || r.supplier_name}
                    onClick={() => r.supplier_id && setOpenId(r.supplier_id)}
                    className="hover:bg-slate-50 cursor-pointer"
                    data-testid={`scorecard-row-${r.supplier_id || r.supplier_name}`}>
                    <td className="px-3 py-2 tabular-nums text-slate-500">{r.rank}</td>
                    <td className="px-3 py-2">
                      <p className="font-bold text-slate-900">{r.supplier_name}</p>
                      <p className="text-[11px] text-slate-500">{r.supplier_email || "no email on file"}</p>
                    </td>
                    <td className="px-3 py-2">
                      <div className="flex items-center gap-2">
                        <span className={`w-6 h-6 rounded-md inline-flex items-center justify-center
                          text-xs font-black ${GRADE_CLS[r.grade] || "bg-slate-200 text-slate-600"}`}>
                          {r.grade || "?"}
                        </span>
                        <span className="w-20 h-1.5 bg-slate-100 rounded-full overflow-hidden hidden lg:inline-block">
                          <span className="block h-full bg-slate-900" style={{ width: `${r.score || 0}%` }} />
                        </span>
                        <span className="text-xs font-bold text-slate-700 tabular-nums">{r.score}</span>
                      </div>
                    </td>
                    <td className="px-3 py-2 text-right tabular-nums">{r.rfqs_invited}</td>
                    <td className="px-3 py-2 text-right tabular-nums">{r.quotes_returned}</td>
                    <td className="px-3 py-2 text-right tabular-nums font-semibold">{pct(r.quote_rate_pct)}</td>
                    <td className="px-3 py-2 text-right tabular-nums">
                      {r.median_response_hours === null ? "—"
                        : r.median_response_hours < 1 ? "<1 hr"
                        : r.median_response_hours < 48 ? `${Math.round(r.median_response_hours)} hrs`
                        : `${Math.round(r.median_response_hours / 24)} days`}
                    </td>
                    <td className="px-3 py-2 text-right tabular-nums">
                      {r.awards_won}
                      {r.award_value > 0 && (
                        <span className="block text-[10px] text-slate-500">{money(r.award_value)}</span>
                      )}
                    </td>
                    <td className="px-3 py-2 text-right tabular-nums">{pct(r.win_rate_pct)}</td>
                    <td className={`px-3 py-2 text-right tabular-nums font-semibold ${
                      r.on_time_pct === null ? "text-slate-400"
                        : r.on_time_pct >= 90 ? "text-emerald-700"
                        : r.on_time_pct >= 70 ? "text-amber-600" : "text-red-600"}`}>
                      {pct(r.on_time_pct)}
                      {r.shipments_measured > 0 && (
                        <span className="block text-[10px] text-slate-500 font-normal">
                          {r.shipments_measured} shipment{r.shipments_measured === 1 ? "" : "s"}
                        </span>
                      )}
                    </td>
                    <td className="px-3 py-2 text-right tabular-nums">{num(r.avg_days_to_ship)}</td>
                    <td className="px-3 py-2 text-right tabular-nums">
                      {r.price_index === null ? "—" : (
                        <span className={r.price_index <= 0.98 ? "text-emerald-700 font-semibold"
                          : r.price_index >= 1.05 ? "text-red-600 font-semibold" : ""}>
                          {r.price_index <= 0.995 ? `${Math.round((1 - r.price_index) * 100)}% under`
                            : r.price_index >= 1.005 ? `${Math.round((r.price_index - 1) * 100)}% over`
                            : "at median"}
                        </span>
                      )}
                    </td>
                    <td className="px-3 py-2 text-right text-xs text-slate-500">{when(r.last_quote_at)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {pending.length > 0 && (
            <section data-testid="scorecard-pending">
              <h3 className="text-[11px] font-black uppercase tracking-wider text-slate-500 mb-2">
                Not enough history to grade ({pending.length})
              </h3>
              <div className="flex flex-wrap gap-2">
                {pending.map((r) => (
                  <button key={r.supplier_id || r.supplier_name}
                    onClick={() => r.supplier_id && setOpenId(r.supplier_id)}
                    className="text-xs bg-white border border-slate-200 rounded-lg px-3 py-2 text-left hover:border-slate-400">
                    <span className="font-semibold text-slate-800 block">{r.supplier_name}</span>
                    <span className="text-slate-500">
                      {r.rfqs_invited} RFQ{r.rfqs_invited === 1 ? "" : "s"} · {r.quotes_returned} quoted
                      {r.spend > 0 ? ` · ${money(r.spend)} spent` : ""}
                    </span>
                  </button>
                ))}
              </div>
            </section>
          )}
        </>
      )}

      {openId && (
        <ScorecardDetail supplierId={openId} days={days} token={token}
          onClose={() => setOpenId(null)} />
      )}
    </div>
  );
}

const Stat = ({ icon: Icon, label, value, sub, tone, testId }) => (
  <div className="bg-white border border-slate-200 rounded-lg p-3" data-testid={testId}>
    <div className={`w-7 h-7 rounded-md inline-flex items-center justify-center mb-1.5 ${tone}`}>
      <Icon className="w-4 h-4" />
    </div>
    <p className="text-[10px] font-black uppercase tracking-wider text-slate-500">{label}</p>
    <p className="text-lg font-black text-slate-900 tabular-nums">{value}</p>
    <p className="text-[11px] text-slate-500">{sub}</p>
  </div>
);

const ScorecardDetail = ({ supplierId, days, token, onClose }) => {
  const [d, setD] = useState(null);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const r = await axios.get(`${API}/admin/supplier-scorecard/${supplierId}`, {
          params: { days }, headers: { Authorization: `Bearer ${token}` },
        });
        if (alive) setD(r.data);
      } catch {
        toast.error("Could not load that supplier's history");
        onClose();
      }
    })();
    return () => { alive = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [supplierId, days, token]);

  const s = d?.summary || {};
  const comps = s.component_scores || {};
  const labels = {
    quote_rate: "Answers RFQs", on_time: "Ships on time",
    win_rate: "Wins the work", speed: "Responds fast", price: "Price vs peers",
  };

  return (
    <div className="fixed inset-0 z-[60] bg-black/40 flex justify-end" onClick={onClose}>
      <div className="bg-white w-full max-w-3xl h-full overflow-y-auto"
        onClick={(e) => e.stopPropagation()} data-testid="scorecard-detail">
        <div className="sticky top-0 bg-white border-b border-slate-200 px-5 py-4 flex items-start justify-between">
          <div>
            <h3 className="text-lg font-black text-slate-900 inline-flex items-center gap-2">
              {d?.supplier?.name || "Loading…"}
              {s.grade && (
                <span className={`w-6 h-6 rounded-md inline-flex items-center justify-center
                  text-xs font-black ${GRADE_CLS[s.grade]}`}>{s.grade}</span>
              )}
            </h3>
            <p className="text-xs text-slate-500 mt-0.5 flex flex-wrap gap-3">
              {d?.supplier?.email && (
                <span className="inline-flex items-center gap-1"><Mail className="w-3 h-3" />{d.supplier.email}</span>
              )}
              {d?.supplier?.phone && (
                <span className="inline-flex items-center gap-1"><Phone className="w-3 h-3" />{d.supplier.phone}</span>
              )}
              {d?.supplier?.source === "ai_discovery" && <span>found by the sourcing crawler</span>}
            </p>
          </div>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-700"
            data-testid="scorecard-detail-close"><X className="w-5 h-5" /></button>
        </div>

        {!d ? (
          <p className="p-8 text-slate-500 inline-flex items-center gap-2">
            <Loader2 className="w-4 h-4 animate-spin" /> Pulling their history…</p>
        ) : (
          <div className="p-5 space-y-6">
            <div className="grid grid-cols-2 sm:grid-cols-5 gap-2">
              {Object.keys(labels).map((k) => (
                <div key={k} className="border border-slate-200 rounded-lg p-2.5">
                  <p className="text-[10px] font-black uppercase tracking-wider text-slate-500 leading-tight">
                    {labels[k]}
                  </p>
                  <p className="text-base font-black text-slate-900 tabular-nums">
                    {comps[k] === undefined ? "—" : comps[k]}
                  </p>
                  <span className="block mt-1 h-1 bg-slate-100 rounded-full overflow-hidden">
                    <span className="block h-full bg-slate-900" style={{ width: `${comps[k] || 0}%` }} />
                  </span>
                </div>
              ))}
            </div>

            <section>
              <h4 className="text-[11px] font-black uppercase tracking-wider text-slate-500 mb-2">
                RFQ history ({d.timeline.length})
              </h4>
              {d.timeline.length === 0 ? (
                <p className="text-sm text-slate-500 border border-dashed border-slate-300 rounded-lg p-4">
                  No RFQ went to them in this window.
                </p>
              ) : (
                <div className="border border-slate-200 rounded-lg overflow-x-auto">
                  <table className="w-full min-w-[720px] text-sm">
                    <thead className="bg-slate-50">
                      <tr>{["Request", "Item", "Asked", "Quoted", "Responded in", "Unit price", "Outcome"].map((h) => (
                        <th key={h} className="px-3 py-2 text-left text-[10px] font-black uppercase tracking-wider text-slate-500">{h}</th>
                      ))}</tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100">
                      {d.timeline.map((e) => (
                        <tr key={e.request_id}>
                          <td className="px-3 py-2">
                            <span className="font-mono text-xs text-slate-700">{e.request_id}</span>
                            <span className="block text-[11px] text-slate-500">{e.hospital_name || ""}</span>
                          </td>
                          <td className="px-3 py-2 text-xs text-slate-700 max-w-[200px]">{e.item || "—"}</td>
                          <td className="px-3 py-2 text-xs text-slate-600">{when(e.invited_at)}</td>
                          <td className="px-3 py-2 text-xs text-slate-600">{when(e.quoted_at)}</td>
                          <td className="px-3 py-2 text-xs tabular-nums">
                            {e.response_hours === undefined ? "—"
                              : e.response_hours < 1 ? "<1 hr" : `${Math.round(e.response_hours)} hrs`}
                          </td>
                          <td className="px-3 py-2 text-xs tabular-nums">
                            {e.unit_price ? `$${e.unit_price.toFixed(2)}` : "—"}
                            {e.lead_time_days !== undefined && e.lead_time_days !== null && (
                              <span className="block text-[10px] text-slate-500">{e.lead_time_days}d lead</span>
                            )}
                          </td>
                          <td className="px-3 py-2">
                            {e.awarded_at ? (
                              <span className="text-[10px] font-black uppercase tracking-wider bg-emerald-50 text-emerald-700 px-1.5 py-0.5 rounded">won</span>
                            ) : e.quoted_at ? (
                              <span className="text-[10px] font-black uppercase tracking-wider bg-slate-100 text-slate-600 px-1.5 py-0.5 rounded">
                                {e.lost_to ? `lost to ${e.lost_to}` : "quoted"}
                              </span>
                            ) : (
                              <span className="text-[10px] font-black uppercase tracking-wider bg-red-50 text-red-700 px-1.5 py-0.5 rounded">no reply</span>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </section>

            <section>
              <h4 className="text-[11px] font-black uppercase tracking-wider text-slate-500 mb-2 inline-flex items-center gap-1.5">
                <Truck className="w-3.5 h-3.5" /> Shipping record ({d.shipments.length})
              </h4>
              {d.shipments.length === 0 ? (
                <p className="text-sm text-slate-500 border border-dashed border-slate-300 rounded-lg p-4">
                  No purchase order with a promised ship date in this window.
                </p>
              ) : (
                <div className="border border-slate-200 rounded-lg overflow-x-auto">
                  <table className="w-full min-w-[680px] text-sm">
                    <thead className="bg-slate-50">
                      <tr>{["PO", "What", "Ordered", "Promised", "Actual", "Result"].map((h) => (
                        <th key={h} className="px-3 py-2 text-left text-[10px] font-black uppercase tracking-wider text-slate-500">{h}</th>
                      ))}</tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100">
                      {d.shipments.map((sh, i) => (
                        <tr key={i}>
                          <td className="px-3 py-2 font-mono text-xs">{sh.po_number || "—"}</td>
                          <td className="px-3 py-2 text-xs text-slate-700 max-w-[180px]">{sh.description || "—"}</td>
                          <td className="px-3 py-2 text-xs">{when(sh.order_date)}</td>
                          <td className="px-3 py-2 text-xs">{when(sh.expected_ship_date)}</td>
                          <td className="px-3 py-2 text-xs">{when(sh.actual_date)}</td>
                          <td className="px-3 py-2">
                            {sh.on_time === null ? (
                              <span className="text-[10px] font-black uppercase tracking-wider bg-slate-100 text-slate-600 px-1.5 py-0.5 rounded">
                                {sh.status || "open"}
                              </span>
                            ) : sh.on_time ? (
                              <span className="text-[10px] font-black uppercase tracking-wider bg-emerald-50 text-emerald-700 px-1.5 py-0.5 rounded">on time</span>
                            ) : (
                              <span className="text-[10px] font-black uppercase tracking-wider bg-red-50 text-red-700 px-1.5 py-0.5 rounded">
                                {sh.days_late}d late
                              </span>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </section>
          </div>
        )}
      </div>
    </div>
  );
};
